/**
 * Tiny typed client for WanEntry's API.
 *
 * All calls are same-origin (`/api/...`). In production the app and API share an
 * origin so the Clerk session cookie authenticates automatically; in local dev
 * the Vite proxy forwards `/api` to `wrangler pages dev`. We also attach the
 * Clerk session token as a Bearer header when one is available, which makes auth
 * work identically regardless of how the request is routed.
 */

export const ENTITY_TYPES = [
  'sole_trader',
  'partnership',
  'company',
  'ccl',
  'charitable_trust',
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  sole_trader: 'Sole Trader',
  partnership: 'Partnership',
  company: 'Company (Ltd)',
  ccl: 'Community Company Limited (CCL)',
  charitable_trust: 'Charitable Trust',
};

export interface Business {
  id: string;
  user_id: string;
  name: string;
  entity_type: string;
  business_address: string | null;
  business_email: string | null;
  business_phone: string | null;
  currency_code: string;
  country_code: string;
  sells_products: number;
  sells_services: number;
  financial_year_start_month: number;
  tax_registered_sales_tax: number;
  tax_registered_goods_tax: number;
  tax_registered_withholding: number;
  tax_registered_paye_employer: number;
  tax_exempt: number;
  tax_exempt_reason: string | null;
  lock_entries_before: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface SaleCategoryDeclaration {
  name: string;
  revenue_account_code: string;
  tax_code: string | null;
}

export interface CreateBusinessInput {
  name: string;
  entity_type: EntityType;
  business_address: string;
  business_email: string;
  business_phone: string;
  sells_products: boolean;
  sells_services: boolean;
  tax_registered_sales_tax: boolean;
  tax_registered_goods_tax: boolean;
  tax_registered_withholding: boolean;
  tax_registered_paye_employer: boolean;
  tax_exempt: boolean;
  tax_exempt_reason: string | null;
  /** Onboarding-declared categories; omit to let the server seed defaults. */
  sale_categories?: SaleCategoryDeclaration[];
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function extractError(data: unknown, status: number): string {
  if (data && typeof data === 'object' && 'error' in data) {
    const value = (data as Record<string, unknown>).error;
    if (typeof value === 'string') return value;
  }
  return `Request failed (${status}).`;
}

async function request<T>(
  path: string,
  token: string | null,
  method: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractError(data, res.status));
  }
  return data as T;
}

export interface Account {
  id: string;
  code: string;
  name: string;
  category: string;
  normal_balance: string;
  is_system: number;
  is_contra: number;
  is_bank_or_cash: number;
  cash_bank_kind: 'CASH' | 'BANK' | null;
  description: string | null;
  active: number;
}

export interface TaxCode {
  /** tax_codes binding id — send this as `tax_code_id` when posting. */
  id: string;
  code: string;
  name: string;
  tax_type: string;
  rate_basis_points: number;
  /** 'PERCENT' (ad valorem) or 'PER_UNIT' (fixed amount per unit). */
  rate_kind: string;
  /** Fixed amount in cents per unit, for PER_UNIT codes. */
  unit_amount_cents: number | null;
  /** Unit a PER_UNIT code is charged per (e.g. 'ticket', 'litre'). */
  unit_label: string | null;
  linked_account_id: string | null;
  active: number;
}

export async function getBusinesses(token: string | null): Promise<Business[]> {
  const data = await request<{ businesses: Business[] }>('/api/businesses', token, 'GET');
  return data.businesses;
}

export async function getAccounts(
  token: string | null,
  businessId: string,
): Promise<Account[]> {
  const data = await request<{ accounts: Account[] }>(
    `/api/accounts?business_id=${encodeURIComponent(businessId)}`,
    token,
    'GET',
  );
  return data.accounts;
}

export async function getTaxCodes(
  token: string | null,
  businessId: string,
): Promise<TaxCode[]> {
  const data = await request<{ taxCodes: TaxCode[] }>(
    `/api/tax-codes?business_id=${encodeURIComponent(businessId)}`,
    token,
    'GET',
  );
  return data.taxCodes;
}

export interface SaleCategory {
  id: string;
  name: string;
  revenue_account_id: string;
  /** tax_codes binding id this category resolves to, or null for no tax. */
  tax_code_id: string | null;
  is_system: number;
  active: number;
}

export async function getSaleCategories(
  token: string | null,
  businessId: string,
): Promise<SaleCategory[]> {
  const data = await request<{ saleCategories: SaleCategory[] }>(
    `/api/sale-categories?business_id=${encodeURIComponent(businessId)}`,
    token,
    'GET',
  );
  return data.saleCategories;
}

export type PaymentMethod = 'CASH' | 'BANK' | 'CREDIT';

/** Cash/Bank money-movement shapes (Pass 7.3); sent as `cash_bank_operation`. */
export const CASH_BANK_OPERATIONS = [
  'TRANSFER',
  'CAPITAL_IN',
  'DRAWINGS',
  'LOAN_RECEIVED',
  'LOAN_REPAYMENT',
  'BANK_FEE',
  'INTEREST_RECEIVED',
  'BILL_PAYMENT',
] as const;
export type CashBankOperation = (typeof CASH_BANK_OPERATIONS)[number];

export interface TransactionEntry {
  id: string;
  transaction_id: string;
  business_id: string;
  account_id: string;
  debit_cents: number;
  credit_cents: number;
  line_order: number;
  created_at: string;
}

export interface TransactionHeader {
  id: string;
  business_id: string;
  transaction_date: string;
  description: string;
  reference: string | null;
  module: string;
  payment_method: string | null;
  counterparty: string | null;
  tax_code_id: string | null;
  tax_inclusive: number;
  cash_bank_operation: string | null;
  replaces_transaction_id: string | null;
  replaced_by_transaction_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Transaction extends TransactionHeader {
  entries: TransactionEntry[];
}

export interface CreateTransactionInput {
  business_id: string;
  module: string;
  transaction_date: string;
  amount_cents: number;
  /** When set, the server resolves the income account + tax from this category. */
  sale_category_id: string | null;
  /** Fallback when no sale_category_id is given; the server resolves from this instead. */
  income_account_id: string | null;
  /** EXPENSE module: the COGS or EXPENSE account to debit. Ignored by INCOME. */
  expense_account_id: string | null;
  /** CASH_BANK module: which money-movement shape this is. Null for other modules. */
  cash_bank_operation: CashBankOperation | null;
  /** CASH_BANK / TRANSFER: the destination cash/bank account (source = settlement_account_id). */
  counter_account_id: string | null;
  /** CASH_BANK / LOAN_REPAYMENT: interest portion in cents; principal = amount − interest. */
  interest_cents: number | null;
  payment_method: PaymentMethod;
  settlement_account_id: string | null;
  tax_code_id: string | null;
  tax_inclusive: boolean;
  /** Units sold; required only when the resolved tax is per-unit (polish 1 wires the UI). */
  quantity: number | null;
  counterparty: string | null;
  description: string | null;
  reference: string | null;
}

export async function getTransactions(
  token: string | null,
  businessId: string,
  module?: string,
): Promise<Transaction[]> {
  const params = new URLSearchParams({ business_id: businessId });
  if (module) params.set('module', module);
  const data = await request<{ transactions: Transaction[] }>(
    `/api/transactions?${params.toString()}`,
    token,
    'GET',
  );
  return data.transactions;
}

export async function createTransaction(
  token: string | null,
  input: CreateTransactionInput,
): Promise<{ transaction: TransactionHeader; entries: TransactionEntry[] }> {
  return request<{ transaction: TransactionHeader; entries: TransactionEntry[] }>(
    '/api/transactions',
    token,
    'POST',
    input,
  );
}

export async function createBusiness(
  token: string | null,
  input: CreateBusinessInput,
): Promise<Business> {
  const data = await request<{ business: Business }>('/api/businesses', token, 'POST', input);
  return data.business;
}
